/*
  customsirge_find_your_vibe.js
  Purpose:  Always-one-open accordion for sections/customsirge_find_your_vibe.liquid
            — horizontal (flex-grow) on desktop, vertical stack on mobile.
            Exactly one category panel is open at all times: clicking a closed
            panel expands it IN PLACE (no DOM reorder) and collapses whichever
            one was open, in the same 0.45s ease; clicking the already-open
            panel is a no-op. Registers ONE Customsirge component, per
            docs/CONVENTIONS.md §4.
  Spec:     extraction/figma/sections/home_find_your_vibe/BRIEF.md → "## Client
            answers (2026-09-15)" Q2 (binding): always-one-open accordion,
            expand in place, 0.45s ease, optional open_on_hover
            (pointer:fine only, default OFF), same one-open rule stacked on
            mobile. Keyboard: "panels are <button aria-expanded>; arrow keys
            move focus" — Tab reaches every TOGGLE button in DOM order (this
            file never sets tabindex on those); arrow keys only relocate
            focus. The CTA <a> inside each panel's body is the one exception
            (FYV-03, loop 1 — see Updated below): only the OPEN panel's CTA
            is a tab stop, so applyState() sets/clears its tabindex.
            spec.json gotcha "COLLAPSED HEIGHTS ARE POSITIONAL, NOT
            PER-CATEGORY": only the drawn centre-open state is in the file, so
            the "panel adjacent to the open one is 440 / the rest are 360"
            desktop rule (assets/customsirge_find_your_vibe.css
            [data-slot-state]) is computed here on init AND on every toggle,
            not just server-rendered once.
  Data:     none — reads only the DOM the section already rendered
            ([data-customsirge-fyv-toggle] buttons + their closest
            [data-customsirge-find-your-vibe] container's data-open-on-hover).
  Renders:  nothing — toggles data-slot-state, the --open/--closed class pair,
            and aria-expanded on markup the section already rendered.
  Owns:     window.Customsirge components.find_your_vibe. CSS lives in
            assets/customsirge_find_your_vibe.css (the [data-slot-state] rules
            do the actual animating; this file only ever flips attributes).
  Owner:    builder(home_find_your_vibe), 2026-09-15
  Updated:  2026-09-28 — the header-height publisher that lived here moved to
            assets/customsirge_kit.js (shared by every fit-one-screen section;
            this copy measured the on-image header's collapsed section wrapper
            and published 0px on the live store).
            2026-09-28 — decision 0059 "fit one screen". The CSS does ALL of the
            scaling maths itself (--customsirge-fyv-u).
            2026-09-28 — client-approved interaction change. Desktop (>=750px
            AND pointer:fine) now expands a CLOSED panel on mouseenter or
            focus and leaves it open until another panel takes over (no
            mouseleave handler), while a click is left to the panel's own <a>
            so it navigates to that panel's collection. Touch/narrow keeps
            tap-to-open and cancels that navigation. The resting open panel
            differs per breakpoint, so state is tracked per breakpoint and
            written to [data-slot-state] (<750px) or [data-slot-state-d]
            (>=750px) — both already server-rendered, so init never moves a
            panel.
            2026-09-15 — fixer(home_find_your_vibe) loop 1 (FYV-03): applyState()
            now also keeps each panel's CTA tabindex/aria-hidden in sync with
            open/closed, matching the section's server-rendered baseline —
            only the OPEN panel's CTA is a tab stop (a11y.keyboard).
*/

(function () {
  'use strict';

  if (!window.Customsirge || typeof window.Customsirge.register !== 'function') {
    return;
  }

  var Customsirge = window.Customsirge;
  var ROOT_CLASS = 'customsirge-find-your-vibe';

  // Two independent questions, deliberately not collapsed into one:
  //   WIDE  — which resting-state attribute the CSS is reading right now
  //           (mobile-first [data-slot-state] vs the >=750px
  //           [data-slot-state-d] staircase). Width only, so it can never
  //           disagree with the stylesheet's own 750px cut.
  //   FINE  — whether this is a real mouse. Only a mouse gets hover-expand and
  //           click-through; a touch tablet wider than 750px still gets the
  //           tap-to-open accordion, just laid out as the desktop row.
  var WIDE = 'screen and (min-width: 750px)';
  var FINE = '(hover: hover) and (pointer: fine)';

  function mq(query) {
    return window.matchMedia ? window.matchMedia(query) : null;
  }

  Customsirge.register('find_your_vibe', {
    selector: '[data-customsirge-find-your-vibe]',
    init: function (el) {
      var toggles = Array.prototype.slice.call(el.querySelectorAll('[data-customsirge-fyv-toggle]'));
      if (toggles.length === 0) return;

      var panels = toggles.map(function (toggle) {
        return toggle.closest('.' + ROOT_CLASS + '__panel');
      });

      var wideMq = mq(WIDE);
      var fineMq = mq(FINE);
      var hoverAllowed = el.dataset.openOnHover !== 'false';

      function isWide() {
        return wideMq ? wideMq.matches : true;
      }
      function isFine() {
        return fineMq ? fineMq.matches : false;
      }
      // Desktop interaction model: hover/focus expands, click navigates.
      function isDesktop() {
        return isWide() && isFine();
      }

      // One open index PER breakpoint, both seeded from what the server
      // rendered — so switching breakpoints (or resizing) restores that
      // breakpoint's own state instead of leaking the other one's.
      function seed(attr) {
        var found = -1;
        panels.forEach(function (panel, i) {
          if (panel && panel.getAttribute(attr) === 'open') found = i;
        });
        return found === -1 ? 0 : found;
      }
      var openIndex = { 'data-slot-state': seed('data-slot-state'), 'data-slot-state-d': seed('data-slot-state-d') };

      function attrName() {
        return isWide() ? 'data-slot-state-d' : 'data-slot-state';
      }

      // Closed panels are served with a small `sizes` (FYV-11) and the browser
      // picks its file from `sizes`, never from the panel's real width — so a
      // panel opened later kept its ~240px download stretched across the open
      // slot. Giving a panel its open-slot `sizes` makes the browser fetch the
      // larger srcset candidate. Only ever upgraded, never set back: a panel
      // that closes again keeps the sharp file it already has.
      function sharpen(panel) {
        var img = panel && panel.querySelector('img[srcset]');
        var open = panel && panel.getAttribute('data-sizes-open');
        if (img && open && img.getAttribute('sizes') !== open) img.setAttribute('sizes', open);
      }

      function applyState() {
        var attr = attrName();
        var index = openIndex[attr];
        var staircase = attr === 'data-slot-state-d';

        panels.forEach(function (panel, i) {
          if (!panel) return;
          var isOpen = i === index;
          var distance = Math.abs(i - index);
          // The desktop staircase needs open/adjacent/far (the 560/440/360
          // heights); the mobile stack only has open/closed.
          var state = isOpen ? 'open' : staircase ? (distance === 1 ? 'adjacent' : 'far') : 'closed';

          panel.setAttribute(attr, state);
          if (isOpen) sharpen(panel);
          panel.classList.toggle(ROOT_CLASS + '__panel--open', isOpen);
          panel.classList.toggle(ROOT_CLASS + '__panel--closed', !isOpen);
          toggles[i].setAttribute('aria-expanded', isOpen ? 'true' : 'false');

          // FYV-03: only the OPEN panel's CTA is a tab stop / exposed to
          // assistive tech — pointer-events is handled purely in CSS,
          // this only covers Tab order.
          var cta = panel.querySelector('.' + ROOT_CLASS + '__cta');
          if (cta) {
            if (isOpen) {
              cta.removeAttribute('tabindex');
              cta.removeAttribute('aria-hidden');
            } else {
              cta.setAttribute('tabindex', '-1');
              cta.setAttribute('aria-hidden', 'true');
            }
          }
        });
      }

      function open(index) {
        var attr = attrName();
        // "opening the already-open panel does nothing" — client answer Q2.
        if (index === openIndex[attr] || index < 0 || index >= toggles.length) return;
        openIndex[attr] = index;
        applyState();
      }

      // Reconcile the server-rendered pair to the live breakpoint at init: the
      // markup already has BOTH resting states, so this never moves a panel on
      // load — it only fixes aria-expanded / CTA tab order for whichever
      // breakpoint we actually came up in.
      applyState();

      var listeners = [];
      function on(target, type, fn) {
        target.addEventListener(type, fn);
        listeners.push({ el: target, type: type, fn: fn });
      }

      toggles.forEach(function (toggle, i) {
        // The panel surface is an <a href> to its collection. On a desktop
        // mouse that click is meant to navigate, so it is left alone. On
        // touch/narrow it is the accordion's tap target, so cancel it.
        on(toggle, 'click', function (event) {
          if (isDesktop()) return;
          event.preventDefault();
          open(i);
        });

        // Hover-expands-and-stays: no mouseleave handler on purpose — the panel
        // stays open until another panel is hovered, focused or tapped.
        on(toggle, 'mouseenter', function () {
          if (!hoverAllowed || !isDesktop()) return;
          open(i);
        });

        // Keyboard parity with hover: tabbing onto a panel's link expands it,
        // Enter then follows the link (native <a> behaviour, not handled here).
        on(toggle, 'focus', function () {
          if (!isDesktop()) return;
          open(i);
        });

        // Arrow keys move focus between panels — they never open one on their
        // own, though on desktop the resulting focus does (see above).
        on(toggle, 'keydown', function (event) {
          var delta = 0;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') delta = 1;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') delta = -1;
          else return;
          event.preventDefault();
          var next = (i + delta + toggles.length) % toggles.length;
          toggles[next].focus();
        });
      });

      // Warm-up: the first time a pointer or the keyboard reaches the panels on
      // a wide screen, every panel gets its open-slot `sizes`, so the sharp
      // files download while the pointer is still travelling rather than after
      // a panel has already opened. A visitor who never comes near the section
      // downloads nothing extra; below 750px the open value resolves to the
      // same 380px, so mobile is unaffected either way.
      var warmed = false;
      function warmUp() {
        if (warmed || !isWide()) return;
        warmed = true;
        panels.forEach(sharpen);
      }
      on(el, 'pointerenter', warmUp);
      on(el, 'focusin', warmUp);

      // Crossing 750px swaps which attribute the CSS reads; re-apply so the
      // newly-authoritative attribute reflects that breakpoint's own state.
      var onChange = function () {
        applyState();
      };
      [wideMq, fineMq].forEach(function (m) {
        if (!m) return;
        if (typeof m.addEventListener === 'function') {
          m.addEventListener('change', onChange);
          listeners.push({ el: m, type: 'change', fn: onChange });
        }
      });

      return function cleanup() {
        listeners.forEach(function (l) {
          l.el.removeEventListener(l.type, l.fn);
        });
      };
    },
  });
})();
